import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Star } from "lucide-react";
import { useLang } from "@/landing/LanguageContext";
import { images } from "@/landing/data";
import { Reveal, Overline, CTAButton } from "@/landing/ui";
import { trackLead } from "@/landing/pixel";

export const BonusSection = () => {
  const { t } = useLang();
  const b = t.bonus;
  return (
    <section className="bg-earth py-24 text-cream lg:py-32" data-testid="bonus-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="mb-14 max-w-2xl">
          <Overline dark>{b.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:text-4xl lg:text-6xl">
            {b.title}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-px overflow-hidden border border-cream/20 bg-cream/20 sm:grid-cols-2 lg:grid-cols-3">
          {b.items.map((item, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="group h-full bg-earth p-8 transition-colors duration-300 hover:bg-carbon/25">
                <Star size={18} className="text-sand" />
                <h3 className="mt-5 font-serif text-xl font-light text-cream">{item.t}</h3>
                <p className="mt-2 font-sans text-sm font-light text-cream/60">{item.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ApplicationSection = () => {
  const { t } = useLang();
  const a = t.application;
  return (
    <section className="relative overflow-hidden bg-carbon text-cream" data-testid="application-section">
      <div className="absolute inset-0">
        <img src={images.closing} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-carbon via-carbon/80 to-carbon" />
      </div>
      <div className="relative mx-auto max-w-4xl px-6 py-28 text-center lg:py-40 lg:px-10">
        <Reveal>
          <Overline dark>{a.overline}</Overline>
          <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] text-balance md:text-5xl lg:text-7xl">
            {a.title}
          </h2>
          <p className="mx-auto mt-8 max-w-2xl font-sans text-base font-light leading-relaxed text-cream/75 lg:text-lg">
            {a.text}
          </p>
          <div className="mt-12 flex flex-col items-center gap-4">
            <CTAButton testid="application-cta" variant="light">
              {a.cta}
            </CTAButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const FaqSection = () => {
  const { t } = useLang();
  const f = t.faq;
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="scroll-mt-20 bg-cream py-24 text-carbon lg:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <Reveal className="mb-12 text-center">
          <Overline>{f.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light md:text-4xl lg:text-5xl">{f.title}</h2>
        </Reveal>

        <div className="divide-y divide-earth/20 border-y border-earth/20">
          {f.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-trigger-${i}`}
                  className="flex w-full items-center justify-between gap-6 py-6 text-left"
                >
                  <span className="font-serif text-lg font-light text-carbon lg:text-2xl">
                    {item.q}
                  </span>
                  <span className="shrink-0 text-earth">
                    {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="whitespace-pre-line pb-7 pr-10 font-sans text-base font-light leading-relaxed text-earth">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t, wa } = useLang();
  const f = t.footer;
  return (
    <footer className="bg-carbon py-16 text-cream" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-10 border-b border-cream/10 pb-12 lg:flex-row lg:items-end">
          <div>
            <p className="font-serif text-3xl tracking-tight lg:text-4xl">
              Escuela <span className="italic text-sand">Metta</span>
            </p>
            <p className="mt-4 max-w-sm font-sans text-sm font-light text-cream/60">{f.tagline}</p>
          </div>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-cta"
            onClick={() => trackLead("footer-cta")}
            className="inline-flex items-center gap-3 rounded-full border border-cream/40 px-7 py-3.5 font-sans text-xs tracking-[0.18em] uppercase text-cream transition-colors hover:bg-cream hover:text-carbon"
          >
            {f.cta} <span>→</span>
          </a>
        </div>
        <div className="mt-8 flex flex-col items-center justify-between gap-3 text-center sm:flex-row sm:text-left">
          <p className="font-sans text-xs text-cream/40">
            © {new Date().getFullYear()} {f.school}. {f.rights}
          </p>
          <p className="font-sans text-xs tracking-[0.2em] uppercase text-cream/40">
            Método METTASOMATIC® · Susana Dakini
          </p>
        </div>
      </div>
    </footer>
  );
};
